import React, { useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import {
  Button,
  DialogField,
  DialogFieldError,
  DialogFieldLabel,
  DialogTextarea,
  TextField,
} from '@aibuild/ui';
import type { DraftDetailsUpdate, UserAppDraft } from '@api/appBuilder';
import { axiosErrorMessage } from '@api/errors';
import AppAgentFields from './AppAgentFields';
import AppRunsOnField from './AppRunsOnField';
import { isDraftConflict, manifestProblemsOf } from './appBuilderErrors';
import { DetailsFooter, DetailsForm, Notice } from './AppDetailsForm.styled';

type Edits = Omit<DraftDetailsUpdate, 'expectedLockVersion'>;

interface AppDetailsFormProps {
  readonly draft: UserAppDraft;
  readonly saving: boolean;
  readonly onSave: (update: DraftDetailsUpdate) => Promise<unknown>;
  /** Fetch the latest draft after a conflict, so the retry is against what is really there. */
  readonly onReload: () => void;
  readonly building?: boolean;
  /** Shown under the instructions: the picture fields, which save on their own. */
  readonly cover?: ReactNode;
}

/**
 * What the creator writes by hand. Only the fields they touched are sent, so
 * whatever the assistant changed meanwhile in the others shows through.
 */
const AppDetailsForm: React.FC<AppDetailsFormProps> = ({ draft, saving, onSave, onReload, building = false, cover }) => {
  const intl = useIntl();
  const [edits, setEdits] = useState<Edits>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);

  const { manifest } = draft;
  const name = edits.name ?? manifest.name.default;
  const tagline = edits.tagline ?? manifest.tagline.default;
  const prompt = edits.prompt ?? manifest.prompt ?? '';
  const allowedSkills = edits.allowedSkills ?? manifest.allowedSkills ?? [];
  const llmModel = edits.llmModel ?? manifest.llmModel ?? '';
  const runsOn = edits.runsOn ?? manifest.runsOn ?? 'CLOUD';
  const alternativeSkills = edits.alternativeSkills ?? manifest.alternativeSkills ?? [];
  const dirty = Object.keys(edits).length > 0;

  const edit = (patch: Edits) => {
    setEdits((current) => ({ ...current, ...patch }));
    setNotice(null);
  };

  const save = () => {
    setProblems([]);
    onSave({ expectedLockVersion: draft.lockVersion, ...edits })
      .then(() => setEdits({}))
      .catch((err: unknown) => {
        if (isDraftConflict(err)) {
          onReload();
          setNotice(
            intl.formatMessage({
              id: 'apps.builder.details.conflict',
              defaultMessage:
                'The assistant updated this app while you were editing. Review your changes and save again.',
            }),
          );
          return;
        }
        const listed = manifestProblemsOf(err);
        setProblems(
          listed.length > 0
            ? listed
            : [
                axiosErrorMessage(
                  err,
                  intl.formatMessage({
                    id: 'apps.builder.details.error',
                    defaultMessage: 'Your changes could not be saved.',
                  }),
                ),
              ],
        );
      });
  };

  return (
    <DetailsForm
      data-testid="app-builder__details"
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      {notice && (
        <Notice role="status" data-testid="app-builder__details-conflict">
          {notice}
        </Notice>
      )}
      <DialogField>
        <DialogFieldLabel htmlFor="app-builder-prompt">
          {intl.formatMessage({
            id: 'apps.builder.details.prompt',
            defaultMessage: 'Instructions for the agent',
          })}
        </DialogFieldLabel>
        <DialogTextarea
          id="app-builder-prompt"
          rows={5}
          value={prompt}
          placeholder={
            building
              ? intl.formatMessage({ id: 'apps.builder.details.promptWriting', defaultMessage: 'Writing the instructions…' })
              : undefined
          }
          maxLength={4000}
          onChange={(event) => edit({ prompt: event.target.value })}
          data-testid="app-builder__prompt"
        />
      </DialogField>
      {cover}
      <DialogField>
        <DialogFieldLabel htmlFor="app-builder-name">
          {intl.formatMessage({ id: 'apps.builder.details.name', defaultMessage: 'Name' })}
        </DialogFieldLabel>
        <TextField
          id="app-builder-name"
          stretch
          value={name}
          maxLength={60}
          onChange={(value) => edit({ name: value })}
          dataTestId="app-builder__name"
        />
      </DialogField>
      <DialogField>
        <DialogFieldLabel htmlFor="app-builder-tagline">
          {intl.formatMessage({ id: 'apps.builder.details.tagline', defaultMessage: 'Tagline' })}
        </DialogFieldLabel>
        <TextField
          id="app-builder-tagline"
          stretch
          value={tagline}
          maxLength={140}
          onChange={(value) => edit({ tagline: value })}
          dataTestId="app-builder__tagline"
        />
      </DialogField>
      <AppRunsOnField value={runsOn} onChange={(value) => edit({ runsOn: value })} />
      <AppAgentFields
        allowedSkills={allowedSkills}
        alternativeSkills={alternativeSkills}
        llmModel={llmModel}
        onChange={edit}
      />
      {problems.map((problem) => (
        <DialogFieldError key={problem}>{problem}</DialogFieldError>
      ))}
      <DetailsFooter>
        <Button
          type="submit"
          variant="filled"
          disabled={!dirty || saving}
          loading={saving}
          dataTestId="app-builder__details-save"
        >
          {intl.formatMessage({ id: 'apps.builder.details.save', defaultMessage: 'Save' })}
        </Button>
      </DetailsFooter>
    </DetailsForm>
  );
};

export default AppDetailsForm;
